<script setup>
import { onBeforeUnmount, onMounted, reactive, ref } from "vue";
import apiClient from "@/apiClient.js";
import SupplierForm from "./SupplierForm.vue";

const props = defineProps({
  initialCompany: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["cancel", "created"]);

const categories = ref([]);
const loadingCategories = ref(true);
const saving = ref(false);
const error = ref("");
const lookupQuery = ref(props.initialCompany.trim());
const lookingUp = ref(false);
const lookupError = ref("");
const lookupResult = ref(null);
let lookupController = null;
const autoFilled = {};
const sourceLabels = {
  company: "Company",
  address: "Registered address",
  vat_number: "VAT number",
  website: "Website",
};
const form = reactive({
  company: props.initialCompany.trim(),
  category_id: "",
  vat_number: "",
  website: "",
  address: "",
  default_vat_treatment: "",
  vat_matching_aliases: props.initialCompany.trim()
    ? [props.initialCompany.trim()]
    : [],
});

const updateForm = (value) => {
  Object.assign(form, value);
};

const loadCategories = async () => {
  loadingCategories.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.get("/api/supplier/categories", {
      suppressErrorAlert: true,
    });
    categories.value = Array.isArray(data) ? data : [];
  } catch (loadError) {
    error.value =
      loadError?.response?.data?.message ??
      "Unable to load supplier categories.";
  } finally {
    loadingCategories.value = false;
  }
};

const lookupSupplier = async () => {
  const company = lookupQuery.value.trim();
  if (lookingUp.value || company.length < 2 || saving.value) return;

  const snapshot = { ...form };
  lookingUp.value = true;
  lookupError.value = "";
  lookupResult.value = null;
  lookupController = new AbortController();
  try {
    const { data } = await apiClient.post(
      "/api/supplier/lookup",
      { company },
      {
        suppressErrorAlert: true,
        timeout: 100000,
        signal: lookupController.signal,
      },
    );
    if (lookupController.signal.aborted) return;
    if (
      form.company !== snapshot.company ||
      lookupQuery.value.trim() !== company
    ) {
      lookupError.value =
        "The supplier name changed during the search. Search again to use the new name.";
      return;
    }
    lookupResult.value = data;
    if (data.status === "matched") {
      for (const field of ["company", "address", "vat_number", "website"]) {
        if (form[field] !== snapshot[field]) continue;
        if (data.details?.[field]) {
          form[field] = data.details[field];
          autoFilled[field] = data.details[field];
        } else if (autoFilled[field] && form[field] === autoFilled[field]) {
          form[field] = "";
          delete autoFilled[field];
        }
      }
    }
  } catch (lookupFailure) {
    if (lookupController.signal.aborted) return;
    lookupError.value =
      lookupFailure?.response?.data?.message ??
      "Unable to search for supplier details. Try again or enter them manually.";
  } finally {
    lookingUp.value = false;
  }
};

const saveSupplier = async () => {
  if (saving.value || lookingUp.value) {
    return;
  }

  if (!form.company.trim() || !form.category_id || !form.address.trim()) {
    error.value = "Company, category and address are required.";
    return;
  }

  saving.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.post(
      "/api/supplier",
      {
        ...form,
        company: form.company.trim(),
      },
      {
        suppressErrorAlert: true,
      },
    );
    emit("created", data);
  } catch (saveError) {
    error.value =
      saveError?.response?.data?.message ?? "Unable to create supplier.";
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadCategories();
  if (lookupQuery.value.length >= 2) lookupSupplier();
});
onBeforeUnmount(() => lookupController?.abort());
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 px-4 py-8"
    role="dialog"
    aria-modal="true"
    aria-labelledby="create-supplier-title"
  >
    <form
      class="max-h-full w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 text-slate-900 shadow-2xl dark:bg-slate-800 dark:text-slate-100"
      @submit.prevent="saveSupplier"
    >
      <div class="flex items-start justify-between gap-3">
        <div>
          <h2 id="create-supplier-title" class="text-lg font-semibold">
            Add Supplier
          </h2>
          <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
            Create this supplier and return it to the transaction assignment.
          </p>
        </div>
        <button
          type="button"
          class="btn"
          :disabled="saving"
          @click="emit('cancel')"
        >
          Close
        </button>
      </div>

      <section
        class="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-900/50"
        aria-label="AI supplier lookup"
      >
        <label for="supplier-lookup-query" class="text-sm font-medium"
          >Find supplier details with AI</label
        >
        <div class="mt-2 flex flex-wrap gap-2">
          <input
            id="supplier-lookup-query"
            v-model="lookupQuery"
            type="text"
            maxlength="255"
            class="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400"
            placeholder="Merchant or company name"
            :disabled="saving"
            @keydown.enter.prevent="lookupSupplier"
          />
          <button
            type="button"
            class="btn disabled:opacity-50"
            :disabled="lookingUp || saving || lookupQuery.trim().length < 2"
            @click="lookupSupplier"
          >
            {{ lookingUp ? "Searching…" : "Search with AI" }}
          </button>
        </div>
        <div
          class="mt-2 text-sm text-slate-600 dark:text-slate-300"
          role="status"
          aria-live="polite"
        >
          <p v-if="lookingUp">
            Searching the web for the legal supplier, registered address and VAT
            number…
          </p>
          <template v-else-if="lookupResult">
            <p v-if="lookupResult.status === 'matched'">
              Supplier details filled where found. Review the details and choose
              a category before creating.
            </p>
            <p v-else-if="lookupResult.status === 'ambiguous'">
              More than one supplier could match. Add a location or website to
              the search.
            </p>
            <p v-else>
              No reliable supplier match found. Refine the search or enter the
              details manually.
            </p>
            <p v-if="lookupResult.notes" class="mt-1">
              {{ lookupResult.notes }}
            </p>
            <ul
              v-if="lookupResult.sources?.length"
              class="mt-2 flex flex-wrap gap-x-4 gap-y-1"
            >
              <li v-for="source in lookupResult.sources" :key="source.field">
                <a
                  :href="source.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sky-700 underline dark:text-sky-300"
                  >{{ sourceLabels[source.field] }} source</a
                >
              </li>
            </ul>
          </template>
          <p v-else>
            Search fills published details only. Unavailable details stay blank.
          </p>
        </div>
        <p
          v-if="lookupError"
          class="mt-2 text-sm text-rose-600 dark:text-rose-300"
          role="alert"
        >
          {{ lookupError }}
        </p>
      </section>

      <div
        v-if="loadingCategories"
        class="mt-6 text-sm text-slate-500 dark:text-slate-300"
      >
        Loading supplier categories…
      </div>
      <SupplierForm
        v-else
        class="mt-6"
        :model-value="form"
        :categories="categories"
        @update:model-value="updateForm"
      />

      <p
        v-if="error"
        class="mt-4 text-sm text-rose-600 dark:text-rose-300"
        role="alert"
      >
        {{ error }}
      </p>

      <div class="mt-6 flex justify-end gap-2">
        <button
          type="button"
          class="btn"
          :disabled="saving"
          @click="emit('cancel')"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="btn accent"
          :disabled="saving || loadingCategories || lookingUp"
        >
          {{ saving ? "Creating…" : "Create & Select" }}
        </button>
      </div>
    </form>
  </div>
</template>
